import { useEffect, useRef, useState, type ReactNode } from 'react';
import { X } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { EddyWordmark } from '~/components/eddy-mark';
import { Tip } from '~/components/ui/tip';
import { HeaderNewButton, HeaderProjectSwitcher, HeaderSaveButton } from '~/components/builder-header/builder-header';
import { ContextMeter } from '~/components/builder-header/context-meter';
import { useProBuilderApp, useProBuilderProjects, type ProBuilderProject } from '~/hooks/queries/use-pro-builder';
import { useProBuilderSessionForBoard } from '~/hooks/queries/use-pro-builder-session';
import { useBindProBuilderApp } from '~/hooks/mutations/use-pro-builder';
import { useCanBuildApps } from '~/hooks/queries/use-is-admin';
import { personFacingMessage } from '~/lib/query-client';
import { useProBuilderOpenStore } from '~/lib/stores/pro-builder-open-store';
import { useBackdropDismiss } from '~/hooks/use-backdrop-dismiss';
import { READING_CARD_PX } from '~/features/chat/reading-prefs';
import { canWorkOnProject, projectDisplayName, SAVE_APP_TOOLTIP, saveAppGate } from './pro-builder-app-rules';
import { PICKER_BIND_ERROR, PICKER_LIST_ERROR } from './pro-builder-app-picker';
import { useProBuilderPickerStore } from './pro-builder-picker-store';
import { useProBuilderBusyReason } from './pro-builder-busy';
import { useProBuilderPublishStore } from './pro-builder-publish-store';
import { PRO_BUILDER_POWERED_BY, PRO_BUILDER_TITLE } from './pro-builder-copy';
import { ProBuilderClaudeSettings } from './pro-builder-claude-settings';
import { ProBuilderCopyAnswerButton } from './pro-builder-copy-answer-button';
import { ProBuilderUploadButton, ProBuilderUploadDialog } from './pro-builder-upload-dialog';
import { closeUploadDialogWhenHidden } from './pro-builder-upload-store';
import { ProBuilderPublishDialog, STARTING_MESSAGE, usePublishRunner } from './pro-builder-publish-dialog';
import { escapeZone, focusReturnTarget, PRO_BUILDER_PANEL_TESTID, proBuilderContextUsage, proBuilderFocusTarget, publishDialogOpen, shouldSwallowEscape, showPublishButton, type FocusAtHide } from './pro-builder-window-rules';

/** Whether `el` shows a keyboard focus ring right now — the open came from the keyboard. */
function matchesFocusVisible(el: HTMLElement): boolean {
  try {
    return el.matches(':focus-visible');
  } catch {
    return false;
  }
}

/**
 * The Pro Builder window (FRONT-3003): the guided Builder's frame, value for
 * value (`features/coding/coding-session-modal.tsx` — same backdrop, panel,
 * ONE 60px header bar, crumb and ×), holding the person's own Claude Code.
 * `pro-builder-window.drift.test.ts` reads both files and fails when a shared
 * frame value diverges, because the Builder is deliberately not edited to
 * share these.
 *
 * Unlike the Builder, closing only HIDES it: Esc is never a close key (Claude
 * Code uses Esc constantly), and the window stays mounted while hidden so the
 * running Claude Code survives — see `pro-builder-open-store.ts`. Width is the
 * Builder's widest reading width: a terminal needs the columns.
 *
 * The header's CONTROLS (app switcher, New, context meter, Save) are not
 * mirrored but shared: both windows render the same components from
 * `components/builder-header/` (FRONT-3077).
 */
const CARD_MAX = READING_CARD_PX.full;

export function ProBuilderWindow({
  open,
  onClose,
  children,
}: {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
}) {
  // Backdrop dismiss keys off where the press started (see use-backdrop-dismiss).
  const backdropDismiss = useBackdropDismiss(onClose);
  const rootRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // What opened the window, and whether from the keyboard, remembered on each
  // hide -> show edge so hiding can hand focus back (see `focusReturnTarget`).
  const openerRef = useRef<HTMLElement | null>(null);
  const openedFromKeyboardRef = useRef(false);
  const wasOpenRef = useRef(false);

  // Focus: on every open the keyboard goes to Claude Code — or to the ended
  // overlay's Reconnect when one is showing (xterm captures Tab, so it could
  // not be reached otherwise). On the FIRST open xterm does not exist yet when
  // this runs (the terminal mounts after the status read answers), so a
  // missing target is waited for with an observer that focuses it once it
  // appears. On hide, focus goes back to the opener only by
  // `focusReturnTarget`'s rule, and is otherwise left where it is.
  useEffect(() => {
    const panel = panelRef.current;
    if (open && !wasOpenRef.current) {
      const active = document.activeElement;
      const opener = active instanceof HTMLElement && active !== document.body && !panel?.contains(active) ? active : null;
      openerRef.current = opener;
      openedFromKeyboardRef.current = !!opener && matchesFocusVisible(opener);
    }
    if (!open && wasOpenRef.current) {
      const active = document.activeElement;
      let focus: FocusAtHide;
      if (!active || active === document.body || active === document.documentElement) focus = 'nowhere';
      else if (rootRef.current?.contains(active)) focus = 'inside-window';
      else focus = 'elsewhere';
      const opener = openerRef.current;
      if (focusReturnTarget(opener, openedFromKeyboardRef.current, focus) === 'opener') opener?.focus();
      openerRef.current = null;
      openedFromKeyboardRef.current = false;
    }
    wasOpenRef.current = open;
    if (!open || !panel) return;
    const focusTarget = () => {
      const target = proBuilderFocusTarget(panel);
      target?.focus();
      return !!target;
    };
    if (focusTarget()) return;
    const observer = new MutationObserver(() => {
      if (focusTarget()) observer.disconnect();
    });
    observer.observe(panel, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [open]);

  // Esc is never a close key here, and it must not reach page handlers
  // underneath — including when focus fell to the body after a click on
  // non-focusable chrome, which no handler on this element would see. A
  // document CAPTURE listener, live only while open: it stops propagation
  // (never the default, never the window) for Escape from this window's chrome
  // or the body. Inside xterm Escape is Claude Code's key and is left alone; a
  // layer above the window (the command palette) keeps its own Escape. Radix
  // layers listen for Escape on the document in capture too — same node, so
  // they still run whatever the registration order.
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      const zone = escapeZone(e.target as HTMLElement | null, rootRef.current, document.body, document.documentElement);
      if (shouldSwallowEscape(e.key, true, zone)) e.stopPropagation();
    };
    document.addEventListener('keydown', onKeyDown, { capture: true });
    return () => document.removeEventListener('keydown', onKeyDown, { capture: true });
  }, [open]);

  return (
    <div
      ref={rootRef}
      // Dialog semantics ONLY while open: three app-wide readers count
      // `[role="dialog"][aria-modal]` in the DOM (Settings' Esc, the Build
      // shortcut, the list shortcuts), and a hidden-but-mounted window must
      // not look like an open modal to any of them.
      role={open ? 'dialog' : undefined}
      aria-modal={open ? true : undefined}
      aria-label={open ? PRO_BUILDER_TITLE : undefined}
      data-testid="pro-builder-window"
      data-open={open ? 'true' : 'false'}
      // Hidden = out of the accessibility tree, unfocusable and not painted,
      // yet still mounted: the terminal inside keeps running.
      inert={!open}
      aria-hidden={!open}
      {...backdropDismiss}
      className="fixed inset-0 z-50 flex items-stretch justify-center"
      style={{
        padding: '56px 24px',
        background: 'rgba(20, 20, 26, 0.42)',
        backdropFilter: 'blur(10px) saturate(0.9)',
        WebkitBackdropFilter: 'blur(10px) saturate(0.9)',
        animation: 'eddy-pro-builder-bg-in 220ms ease-out',
        display: open ? undefined : 'none',
      }}
    >
      <style>{`
        @keyframes eddy-pro-builder-bg-in { from { opacity: 0 } to { opacity: 1 } }
        @keyframes eddy-pro-builder-panel-in {
          from { opacity: 0; transform: translateY(8px) }
          to   { opacity: 1; transform: translateY(0) }
        }
      `}</style>

      <div
        ref={panelRef}
        onClick={(e) => e.stopPropagation()}
        data-testid={PRO_BUILDER_PANEL_TESTID}
        className="flex flex-col w-full overflow-hidden bg-card text-foreground border border-border"
        style={{
          maxWidth: CARD_MAX,
          maxHeight: '100%',
          minHeight: 420,
          borderRadius: 'var(--radius-xl-ds)',
          boxShadow: 'var(--shadow-lg)',
          animation: 'eddy-pro-builder-panel-in 240ms ease-out',
        }}
      >
        {children}
      </div>
    </div>
  );
}

/** Which body the header sits over: the terminal, or the "New app" view (absent: loading, error, org-off or the first-time picker). */
export type ProBuilderHeaderView = 'terminal' | 'new';

/**
 * The window's ONE 60px bar, in the guided Builder's order and with its
 * controls (FRONT-3077 header round): the "EDDY / Pro Builder" crumb, the app
 * switcher (folder icon + the bound app's name), "+ New", then — at the right —
 * a quiet "Runs Claude Code", the context meter, the Save disc (people
 * who may build apps), Upload (documents into the app's Uploaded Documents folder), the Claude Code settings gear (FRONT-3228: the
 * auto-compact threshold, the one setting that lives here, with its own "Restart Claude Code") and the ×.
 * Nothing else belongs here — the connection, billing and the credential restart offer live in Settings →
 * Pro Builder.
 *
 * Over the terminal everything shows. Over the "New app" view the switcher
 * stays (picking the bound app again returns to its terminal) while New, the
 * meter and Save hide — the Builder does the same while creating. Over every
 * other body (loading, error, org-off, the first-time picker) only the crumb,
 * the "Runs Claude Code" label, the gear and the × show: there is nothing to switch from, and the
 * picker has its own new-app field.
 */
export function ProBuilderHeaderBar({ onClose, view }: { onClose: () => void; view?: ProBuilderHeaderView }) {
  const bound = view !== undefined;
  const { canBuildApps } = useCanBuildApps();
  // FRONT-3077: the bound app. Read only where a bound body shows, which
  // renders only when the organization switch is on.
  const boundApp = useProBuilderApp(bound);
  const project = bound ? (boundApp.data?.project ?? null) : null;
  const setCreating = useProBuilderPickerStore((s) => s.setCreating);
  const publishingBound = useProBuilderPublishStore((s) => (project ? s.running.includes(project.name) : false));
  const row = useProBuilderPublishStore((s) => (project ? s.rows[project.name] : undefined));
  // One decision each: a control is disabled iff it has a reason, and the reason is its hint AND its description.
  const blockedReason = useProBuilderBusyReason(project?.name);
  const save = saveAppGate({
    bound: project !== null,
    folderBound: bound && !!boundApp.data?.folder,
    publishing: publishingBound,
    progress: row?.phase === 'publishing' ? row.message : STARTING_MESSAGE,
    switching: blockedReason !== null,
    blockedReason: project?.blockedReason ?? null,
  });
  const { publish } = usePublishRunner();
  // The window is hidden, not unmounted, so the details dialog must not outlive it,
  // and the session read (the context meter) polls only while it shows.
  const windowOpen = useProBuilderOpenStore((s) => s.open);
  const detailsFor = useProBuilderPublishStore((s) => s.detailsFor);
  const closeDetails = useProBuilderPublishStore((s) => s.closeDetails);
  useEffect(() => {
    if (!windowOpen) closeDetails();
  }, [windowOpen, closeDetails]);
  // Same for the upload dialog: it closes whenever the terminal is not shown (window hidden OR another view), or it would pop open again on its own when the terminal returns.
  const terminalShown = windowOpen && view === 'terminal';
  useEffect(() => {
    closeUploadDialogWhenHidden(terminalShown);
  }, [terminalShown]);
  const session = useProBuilderSessionForBoard(view === 'terminal' && windowOpen);
  return (
    <>
      <div className="flex items-center gap-3 h-[60px] px-4 shrink-0 border-b border-border">
        <div className="flex items-center gap-1.5 shrink-0" data-testid="pro-builder-window-crumb">
          <EddyWordmark size={15} className="shrink-0 text-foreground" />
          <span aria-hidden className="text-[length:var(--text-md)] text-muted-foreground/50">
            /
          </span>
          <span className="text-[length:var(--text-md)] font-semibold tracking-[-0.2px] text-foreground whitespace-nowrap">
            {PRO_BUILDER_TITLE}
          </span>
        </div>
        <span className="h-5 w-px bg-border shrink-0" aria-hidden />
        {project && <ProBuilderAppSwitcher project={project} blockedReason={blockedReason} />}
        {view === 'terminal' && (
          <HeaderNewButton
            onClick={() => setCreating(true)}
            label="New app"
            tooltip="Start a new app"
            disabledReason={blockedReason}
            testId="pro-builder-new-app"
          />
        )}
        <div className="flex-1 min-w-[8px]" />
        <span data-testid="pro-builder-powered-by" className="truncate text-[length:var(--text-sm)] text-muted-foreground">
          {PRO_BUILDER_POWERED_BY}
        </span>
        <div className="flex items-center gap-1.5 shrink-0">
          {view === 'terminal' && <ContextMeter usage={proBuilderContextUsage(session?.context)} testId="pro-builder-context-meter" />}
          {view === 'terminal' && session?.id && <ProBuilderCopyAnswerButton sessionId={session.id} />}
          {view === 'terminal' && showPublishButton(true, canBuildApps) && (
            // Saving IS publishing: it runs the build and replaces the live app,
            // with no dialog first. The dialog is the details view behind the
            // failure and warnings toasts' "Details".
            <HeaderSaveButton
              onClick={() => {
                if (project && !save.pending) void publish(project.name);
              }}
              pending={save.pending}
              disabledReason={save.disabledReason}
              tooltip={SAVE_APP_TOOLTIP}
              testId="pro-builder-save"
            />
          )}
          {view === 'terminal' && (
            <>
              <ProBuilderUploadButton />
              <ProBuilderUploadDialog folder={boundApp.data?.folder ?? null} windowOpen={windowOpen} />
            </>
          )}
          {project && canBuildApps && (
            <ProBuilderPublishDialog
              open={publishDialogOpen(detailsFor === project.name, windowOpen)}
              onOpenChange={(open) => {
                if (!open) closeDetails();
              }}
              project={project}
            />
          )}
          <ProBuilderClaudeSettings />
          <Tip label="Close — Claude Code keeps running">
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              data-testid="pro-builder-close"
              className="size-[34px] shrink-0 inline-flex items-center justify-center rounded-[10px] text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            >
              <X size={18} />
            </button>
          </Tip>
        </div>
      </div>
      {blockedReason !== null && (
        // A hover hint is no explanation on touch: while the controls wait, say why in plain text.
        <p role="status" data-testid="pro-builder-busy" className="shrink-0 px-4 py-1 text-[length:var(--text-xs)] text-muted-foreground border-b border-border">
          {blockedReason}
        </p>
      )}
    </>
  );
}

/**
 * The header's app switcher: the guided Builder's project switcher, listing
 * the person's Pro Builder folders. The list read is LAZY — enabled only once
 * the menu opens — because `GET /api/pro-builder/projects` runs the all-apps
 * sync and must never fire on every header render; until it answers the menu
 * says so in a disabled row, and a failed read says so instead. Folders the
 * server cannot open (an invalid name) are left out: the first-time picker is
 * where their reason is shown. Picking the bound app returns from the "New
 * app" view; picking another binds it (`useBindProBuilderApp`, which restarts
 * the terminal when the server ended one).
 */
function ProBuilderAppSwitcher({ project, blockedReason }: { project: ProBuilderProject; blockedReason: string | null }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const projects = useProBuilderProjects(menuOpen);
  const bind = useBindProBuilderApp();
  const setCreating = useProBuilderPickerStore((s) => s.setCreating);
  const list = projects.data?.projects;
  const onSelect = (folder: string) => {
    if (folder === project.name) {
      setCreating(false);
      return;
    }
    bind.mutate({ folder }, { onError: (err) => toast.error(personFacingMessage(err, PICKER_BIND_ERROR)) });
  };
  return (
    <HeaderProjectSwitcher
      title={projectDisplayName(project)}
      options={(list ?? []).filter(canWorkOnProject).map((p) => ({ id: p.name, label: projectDisplayName(p) }))}
      currentId={project.name}
      onSelect={onSelect}
      switchLabel="Switch app"
      disabledReason={blockedReason}
      loading={!list && !projects.isError}
      loadingLabel="Loading your apps…"
      error={!list && projects.isError ? personFacingMessage(projects.error, PICKER_LIST_ERROR) : null}
      onOpenChange={setMenuOpen}
      testId="pro-builder-app-switcher"
      itemTestId="pro-builder-app-switcher-item"
      itemIdAttribute="data-folder"
    />
  );
}
